import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check } from "@phosphor-icons/react";
import { EVENT_ICON_TYPES } from "@/lib/event-icons";

const MARGIN = 12;
const POP_WIDTH = 380;
const POP_MAX_HEIGHT = 540;

export default function EventPopover({ data, project, onClose, onSubmit }) {
  const defaultField = data?.field_ref || "";
  const defaultDate = data?.event_date || "";
  const [selected, setSelected] = useState(
    EVENT_ICON_TYPES.find((o) => o.type === "milestone") || EVENT_ICON_TYPES[0]
  );
  const [label, setLabel] = useState("");
  const [comment, setComment] = useState("");
  const [fieldRef, setFieldRef] = useState(defaultField);
  const [eventDate, setEventDate] = useState(defaultDate);
  const popRef = useRef(null);
  // Position is derived from the anchor rect clamped to the viewport. Recomputed on resize so
  // the popover never ends up partly offscreen (which previously required ctrl+- to reach).
  const [pos, setPos] = useState({ top: MARGIN, left: MARGIN, maxHeight: POP_MAX_HEIGHT });

  useEffect(() => {
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const compute = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxH = Math.min(POP_MAX_HEIGHT, vh - MARGIN * 2);
      // Always center the popover in the viewport — this is a modal-style
      // dialog, not an anchored dropdown, so it should feel consistent and
      // predictable regardless of which bar the user clicked.
      const top = Math.max(MARGIN, Math.round((vh - maxH) / 2));
      const left = Math.max(MARGIN, Math.round((vw - POP_WIDTH) / 2));
      setPos({ top, left, maxHeight: maxH });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      type: selected.type,
      icon_label: label.trim() || selected.label,
      color: selected.color,
      comment: comment.trim(),
      field_ref: fieldRef || null,
      event_date: eventDate || null,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/30 backdrop-blur-[1px]" data-testid="event-popover-overlay">
      <div
        ref={popRef}
        className="absolute bg-white border border-slate-300 rounded-md shadow-[0_24px_64px_rgba(15,23,42,0.25)] fade-up flex flex-col"
        style={{ top: pos.top, left: pos.left, width: POP_WIDTH, maxHeight: pos.maxHeight }}
        data-testid="event-popover"
      >
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="p-4 space-y-3 overflow-y-auto custom-scrollbar flex-1 min-h-0">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Icon Type
            </Label>
            <div className="grid grid-cols-5 gap-1.5 mt-2">
              {EVENT_ICON_TYPES.map((o) => {
                const Icon = o.icon;
                const active = selected.type === o.type;
                return (
                  <button
                    key={o.type}
                    type="button"
                    onClick={() => setSelected(o)}
                    className={`h-9 w-full rounded-md border flex items-center justify-center transition-all ${
                      active ? "border-slate-950 scale-105" : "border-slate-200 hover:border-slate-400"
                    }`}
                    title={`${o.label} — ${o.description}`}
                    data-testid={`icon-option-${o.type}`}
                    style={{ background: active ? o.color : "white" }}
                  >
                    <Icon
                      size={16}
                      weight={active ? "fill" : "bold"}
                      style={{ color: active ? "white" : o.color }}
                    />
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-slate-500 leading-snug">
              <span className="font-semibold text-slate-700">{selected.label}:</span>{" "}
              {selected.description}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Label <span className="normal-case tracking-normal text-slate-400">(optional)</span>
            </Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={selected.label}
              className="h-9 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-sm"
              data-testid="event-label-input"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Comment
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Add more context — appears on hover…"
              className="mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-sm resize-none"
              data-testid="event-comment-input"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Pin to Date
            </Label>
            <Input
              type="date"
              value={eventDate || ""}
              onChange={(e) => setEventDate(e.target.value)}
              className="h-9 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-sm font-mono-ibm"
              data-testid="event-date-input"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Leave empty to pin to a date column below or the task's earliest date.
            </div>
          </div>

          {project.date_columns?.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                Or pin to a date column
              </Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <button
                  type="button"
                  onClick={() => setFieldRef("")}
                  className={`px-2 h-6 text-[11px] rounded-md border ${fieldRef === "" ? "bg-slate-950 text-white border-slate-950" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
                  data-testid="field-ref-none"
                >
                  None
                </button>
                {project.date_columns.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setFieldRef(c.id)}
                    className={`px-2 h-6 text-[11px] rounded-md border ${fieldRef === c.id ? "bg-slate-950 text-white border-slate-950" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
                    data-testid={`field-ref-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          </div>

          <div className="flex justify-end gap-2 p-3 border-t border-slate-200 bg-white rounded-b-md shrink-0">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-md border-slate-300 text-xs">
              Cancel
            </Button>
            <Button type="submit" className="h-9 rounded-md bg-slate-950 hover:bg-slate-800 text-white text-xs" data-testid="event-submit-btn">
              <Check size={12} weight="bold" />
              <span className="ml-1.5">Add</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
